<template>
  <b-container>
    <b-row v-if="loading">
      <b-col class="cols-6 d-flex justify-content-center mt-10">
        <span
          class="spinner-border spinner-border-lg center-spinner text-primary m-auto"
        ></span>
      </b-col>
    </b-row>
    <b-row>
      <b-col class="col-12 col-sm-10 col-lg-6 mx-auto">
        <div v-if="!loading" class="card mt-10">
          <PulsCardHeader
            :title="$t('user.header')"
            :leftAction="{
              to: { name: 'overview', title: $t('user.button.skip') }
            }"
          />
          <b-card-body>
            <b-row v-for="(attribute, name) in defaultAttributes" :key="name">
              <b-col
                v-if="!attribute.isHidden && attributeNameShouldBeVisible(name)"
              >
                <div
                  v-if="attribute.type === 'string'"
                  class="d-flex align-items-center mb-3"
                >
                  <i :class="`bi bi-${attribute.icon} fs-2 pt-1 me-2`"></i>
                  <PulsFormInput
                    class="flex-grow-1 me-6"
                    :placeholder="attribute.placeholder"
                    v-model="attribute.value"
                  />
                </div>

                <div
                  v-if="attribute.type === 'select'"
                  class="d-flex align-items-center mb-3"
                >
                  <i
                    v-if="attribute.icon !== 'account_box'"
                    :class="`bi bi-${attribute.icon} fs-2 pt-2 me-2`"
                  ></i>
                  <i
                    v-else
                    :class="`bi bi-patch-question-fill fs-2 pt-2 me-2`"
                  ></i>
                  <PulsFormSelect
                    class="flex-grow-1"
                    :options="attribute.options"
                    v-model="attribute.value"
                  />
                  <i
                    :id="name + '-help'"
                    v-if="attribute.info"
                    class="bi bi-question fs-3 text-info md-18 help-icon"
                  ></i>
                  <b-tooltip
                    :target="name + '-help'"
                    placement="top"
                    triggers="hover blur"
                    delay="100"
                  >
                    {{ attribute.info }}
                  </b-tooltip>
                </div>
              </b-col>
            </b-row>

            <div v-if="error">
              <p class="mt-3 ml-5 text-danger">{{ error.message }}</p>
            </div>
          </b-card-body>

          <b-card-footer class="d-flex justify-content-end align-items-start">
            <b-button
              class="me-5"
              variant="secondary"
              @click="cancel"
              type="submit"
              :disabled="!changesHaveBeenMade"
            >
              <i class="bi bi-x-circle-fill"></i>
              {{ $t('user.button.cancel') }}
            </b-button>
            <b-button
              variant="primary"
              @click="save"
              type="submit"
              :disabled="!changesHaveBeenMade"
            >
              <i class="bi bi-save-fill"></i>
              {{ $t('user.button.submit') }}
            </b-button>
          </b-card-footer>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import {
  PulsFormSelect,
  PulsFormInput,
  PulsCardHeader
} from '@puls-solutions/puls-ui-components'

import { saveAttributes } from '../services/AwsAmplifyService'

export default {
  name: 'User',
  components: {
    PulsCardHeader,
    PulsFormInput,
    PulsFormSelect
  },
  data() {
    return {
      defaultAttributes: {
        given_name: {
          icon: 'person-fill',
          type: 'string',
          placeholder: this.$t('user.attributes.given_name.label'),
          value: null
        },
        family_name: {
          icon: 'person-fill',
          type: 'string',
          placeholder: this.$t('user.attributes.family_name.label'),
          value: null
        }
      }
    }
  },
  mounted() {
    this.updateAttributesFromUserAttributes(this.userAttributes)
    this.reRenderAttributesForLocale()
  },
  watch: {
    '$i18n.locale'() {
      this.reRenderAttributesForLocale()
    }
  },
  computed: {
    changesHaveBeenMade() {
      let changesMade = false
      Object.keys(this.defaultAttributes)
        .filter((key) => this.defaultAttributes[key].value)
        .forEach((key) => {
          const value = this.defaultAttributes[key].value
          if (!this.userAttributes[key] || this.userAttributes[key] !== value) {
            changesMade = true
          }
        })

      return changesMade
    },
    error() {
      return this.$store.getters.userDetailsError
    },
    loading() {
      return this.$store.getters.userDetailsLoading
    },
    organizationConfiguredAttributes() {
      if (
        window.localStorage.orgData &&
        JSON.parse(window.localStorage.orgData).attributes
      ) {
        return JSON.parse(window.localStorage.orgData).attributes
      } else {
        return []
      }
    },
    userAttributes() {
      return this.$store.getters.loggedInUser.signInUserSession.idToken.payload
    }
  },
  methods: {
    reRenderAttributesForLocale() {
      const attributes = {}
      Object.keys(this.defaultAttributes).forEach((key) => {
        const attribute = this.defaultAttributes[key]
        if (attribute.placeholder) {
          this.defaultAttributes[key].placeholder = this.$t(
            `user.attributes.${key}.label`
          )
        }
        if (attribute.info) {
          this.defaultAttributes[key].info = this.$t(
            `user.attributes.${key}.info`
          )
        }
        if (attribute.options) {
          attribute.options.forEach((option, i) => {
            if (option.value !== null) {
              this.defaultAttributes[key].options[i].text = this.$t(
                `user.attributes.${key}.${option.value}`
              )
            } else {
              this.defaultAttributes[key].options[i].text = this.$t(
                `user.attributes.${key}.label`
              )
            }
          })
        }
        attributes[key] = attribute
      })
      this.defaultAttributes = attributes
    },
    attributeNameShouldBeVisible(attributeName) {
      return (
        this.organizationConfiguredAttributes.length === 0 || // no custom attributes found
        !this.organizationConfiguredAttributes
          .map((a) => a.name)
          .includes(attributeName) || // or attribute is not one of the custom attributes
        (this.organizationConfiguredAttributes
          .map((a) => a.name)
          .includes(attributeName) && // or it is, and it is enabled
          this.organizationConfiguredAttributes.filter(
            (a) => a.name === attributeName
          )[0].data.enabled)
      )
    },
    save() {
      if (this.changesHaveBeenMade) {
        const attributes = this.selectedAttributes()
        saveAttributes(attributes, this.$store, this.$router)
      } else {
        this.$router.push('/overview')
      }
    },
    cancel(e) {
      if (e) {
        e.preventDefault()
      }
      this.clearAttributesThatWereNotSetFromStart(this.userAttributes)
      this.updateAttributesFromUserAttributes(this.userAttributes)
      this.reRenderAttributesForLocale()
    },
    updateAttributesFromUserAttributes(userAttributes) {
      Object.keys(userAttributes).forEach((userAttribute) => {
        if (Object.keys(this.defaultAttributes).includes(userAttribute)) {
          this.defaultAttributes[userAttribute].value =
            userAttributes[userAttribute]
        }
      })
    },
    clearAttributesThatWereNotSetFromStart(userAttributes) {
      const userAttributeKeys = Object.keys(userAttributes)
      Object.keys(this.defaultAttributes)
        .filter((key) => !userAttributeKeys[key])
        .forEach((key) => {
          this.defaultAttributes[key].value = null
        })
    },
    // Collect all attributes that have values and reformat as needed
    selectedAttributes() {
      const attributes = {}
      Object.keys(this.defaultAttributes)
        .filter((key) => this.defaultAttributes[key].value)
        .forEach((key) => {
          const attribute = this.defaultAttributes[key]
          let value = attribute.value
          if (attribute.type === 'date') {
            if (attribute.value.toISOString) {
              value = attribute.value.toISOString().split('T')[0]
            }
          }
          attributes[key] = value
        })

      return attributes
    }
  }
}
</script>

<style lang="scss" scoped>
.content {
  padding: 0;
}

.center-spinner {
  margin: 0 auto !important;
  float: none !important;
}

::-webkit-input-placeholder,
::-moz-placeholder {
  margin-left: 10px !important;
}

@media (max-width: 340px) {
  .date-input {
    width: 120px;
  }

  .form-body {
    margin-left: 0px;
    padding-left: 0px;
  }
}
</style>

<style>
.help-icon {
  cursor: help;
}
</style>
